"use client";
import { useLayout } from "@/providers/LayoutProvider/context";
import { isTacticStaff } from "@gettactic/helpers/src/featureFlags";
import { convertTZ, parseApiDate } from "@/lib/utils";
import { Division, IResource, IUserFull, Office, Tag } from "@gettactic/api";
import { TIME_SELECT_INTERVAL_MINUTES } from "@cmp-src/date-time/constants";
import {
  createMapLink,
  StartEndTimeMode,
  UseDisclosureReturn,
  useDisclosure
} from "@gettactic/helpers";
import {
  UseEventHandlerManagerReturn,
  useEventHandlerManager
} from "@gettactic/helpers/src/hooks/useEventHandlerManager";
import { InteractiveZonesInfo } from "@gettactic/helpers/src/map/types";
import { setStorageFloor } from "@gettactic/helpers/src/resources/setStorageFloor";
import {
  IFloorWithResources,
  IResourceWithSchedules,
  IUserWithResource,
  setEndTimeWithDayOverflow,
  useAuthenticated,
  useDefaultOfficeHours,
  useGetAmenities,
  useOfficeResources
} from "@gettactic/hooks";
import { MapCanvasValue } from "@gettactic/map-builder/src/components/map/contexts/MapCanvasContext";
import { MapInteractiveZoneActionOptions } from "@gettactic/map-builder/src/types";
import { addMinutes, format, isSameDay, parseISO } from "date-fns";
import { fromZonedTime } from "date-fns-tz";
import React, { useMemo, useRef } from "react";
import { useGlobalSearch } from "@/features/global-search";
import { MapNavigationParams } from "@/features/global-search/GlobalSearchProvider";
import {
  UseMapViewOverlaysPopperReturn,
  useMapViewOverlaysPopper
} from "@/features/map/components/MapView/useMapViewOverlaysPopper";
import { MapModalParams } from "@/features/map/components/modal";
import { useReserve } from "@/features/reserve/providers/context";
import { useRouter } from "@/lib/useRouter";

// Module-level variable to track pending office navigation
// (persists across component remounts caused by shallow routing)
let pendingOfficeNavigation: string | null = null;

interface MapProviderEvents {
  onHighlightResource: (resource: IResource | null, userId?: string) => void;
  onFocusResource: (
    resource: IResource | null,
    options?: { noZoom?: boolean }
  ) => void;
  onLocateResource: (resource: IResource | null) => void;
  // Fired when a "start reservation" path lands on a ticket-gated resource:
  // it has no direct booking flow, so the request wizard opens instead.
  onRequestGatedBooking: (resource: IResource) => void;
  onHighlightUserResource: (resources: IResource[] | null) => void;
  onChangeFloor: (
    newFloor: IResource | null,
    oldFloor: IResource | null
  ) => void;
}

export interface MapPageOptions {
  schedulable?: IResourceWithSchedules[];
  floors?: IResource[];
  floorsWithResources?: IFloorWithResources[];
  usersAtOffice?: IUserWithResource[];
  resources?: IResource[];
  allResources?: IResource[];
  floor: IResource | null;
  currentOffice: Office;
  startDate: Date | null;
  endDate: Date | null;
  timeMode?: StartEndTimeMode;
  setTimeMode: (timeMode: StartEndTimeMode) => void;
  isDataLoading?: boolean;
  selectedResource: IResource | null;
  isMapEditing: boolean;
  isEmbedded: boolean;
  isMapBuilderMap: boolean;
  highlightedResource: IResource | null;
  poppers: UseMapViewOverlaysPopperReturn;
  openResourceModal: boolean;
  editGroupId: string | null;
  mapBuilderData: MapCanvasValue | undefined;
  editingMapDisclosure: UseDisclosureReturn;
  resourceIdHovering: string | null;
  focusedResource: string | null;
  events: UseEventHandlerManagerReturn<MapProviderEvents>;
  amenities: Tag[];
  amenitiesFilter: Tag[];
  userFocusDisclosure: UseDisclosureReturn<IUserFull>;
  mapFiltersDisclosure: UseDisclosureReturn;
  zonesInfo: InteractiveZonesInfo;
  locateResourceId: string | null;
  liveStartDate: Date | null;
  liveEndDate: Date | null;
  mapChangesCount: number;
  mapBuilderSaveCount: number;
  forceMapBuilder: boolean;
  filterQuery: string;
  setFilterQuery: React.Dispatch<React.SetStateAction<string>>;
  isReservationMode: boolean;
  /** When true the map is used purely to pick a resource: the reserve/details popup is suppressed. */
  selectionMode: boolean;
  /** When true, clicking an empty point on the map drops a pin to file a workplace request. */
  isPinRequestMode: boolean;
  setIsPinRequestMode: React.Dispatch<React.SetStateAction<boolean>>;
  resourceInReservation: string | null;
  fullscreenMapDisclosure: UseDisclosureReturn;
  defaultTab?: MapModalParams["defaultTab"];
  setSection: (section: MapModalParams["defaultTab"]) => void;
  selectedUsersInCalendarIntegrations: {
    value: string;
    label: string;
    picture?: string;
    profile_photo_url?: string;
  }[];
  mapInstanceId: string;
  isReservationCardIsOpen: boolean;
}

export interface MapStateActions {
  setFloor: (newFloor: IResource | null) => void;
  updateFloorMapData: (mapData: string) => void;
  changeOffice: (officeId: string) => void;
  /** Switch office and land on a specific floor once its floors load
   *  (zone-link navigation, e.g. campus map → office floor). */
  navigateToOfficeFloor: (officeId: string, floorId?: string) => void;
  /** Campus (division) whose map is being viewed instead of a floor.
   *  Any office/floor navigation clears it. */
  viewingDivision: Division | null;
  setViewingDivision: React.Dispatch<React.SetStateAction<Division | null>>;
  changeEndTime: (date: Date | null) => void;
  changeStartDate: (date: Date | null) => void;
  changeTimeRange: (start: Date, end: Date) => void;
  setSelectedResource: React.Dispatch<React.SetStateAction<IResource | null>>;
  setHighlightedResource: React.Dispatch<
    React.SetStateAction<IResource | null>
  >;
  setOpenResourceModal: React.Dispatch<React.SetStateAction<boolean>>;
  setEditGroupId: React.Dispatch<React.SetStateAction<string | null>>;
  highlightResource: (
    resId: string | null,
    target: Element | MapInteractiveZoneActionOptions | null
  ) => void;
  setForceMapBuilder: React.Dispatch<React.SetStateAction<boolean>>;
  setResourceIdHovering: React.Dispatch<React.SetStateAction<string | null>>;
  setFocusedResource: React.Dispatch<React.SetStateAction<string | null>>;
  startReserve: (resource: IResource) => void;
  setResourcesIdFilter: React.Dispatch<React.SetStateAction<string[]>>;
  setStartDate: React.Dispatch<React.SetStateAction<Date | null>>;
  applyAmenitiesFilter: (ids: string[]) => void;
  selectResource: (
    resId: string,
    center?: boolean,
    callback?: () => void,
    officeId?: string,
    floorId?: string,
    date?: Date,
    preserveData?: boolean
  ) => void;
  setLocateResourceId: React.Dispatch<React.SetStateAction<string | null>>;
  setZonesInfo: React.Dispatch<React.SetStateAction<InteractiveZonesInfo>>;
  setMapChangesCount: React.Dispatch<React.SetStateAction<number>>;
  setSelectedUsersInCalendarIntegrations: React.Dispatch<
    React.SetStateAction<
      {
        value: string;
        label: string;
        picture?: string;
        profile_photo_url?: string;
      }[]
    >
  >;
  onFloorReady: (floorId: string) => void;
}

export type MapPageState = MapPageOptions &
  MapStateActions & {
    isOnReservationModal?: boolean;
    isReservationCardIsOpen?: boolean;
  };

export const MapPageContext = React.createContext<MapPageState | null>(null);

export interface EditingMapDisclosureData {
  mode: "svg" | "map-builder";
}

export interface MapProviderProps {
  isEmbedded?: boolean;
  officeId?: string;
  floorId?: string;
  startDate?: string | null;
  // startTime?: string | null;
  initialFloorId?: string | null;
  initialSearchText?: string;
  resourceId?: string;
  reservationModalIsOpen?: boolean;
  initialEndDate?: string | null;
  isOnReservationModal?: boolean;
  isReservationCardIsOpen?: boolean;
  section?: MapModalParams["defaultTab"];
  initialTimeMode?: StartEndTimeMode;
  disableUrlOfficeSync?: boolean;
  /** Use the map only to pick a resource (suppresses the reserve/details popup). */
  selectionMode?: boolean;
}

export const MapProvider: React.FC<
  React.PropsWithChildren<MapProviderProps>
> = ({
  children,
  isEmbedded = false,
  officeId: controlledOfficeId,
  floorId: controlledFloorId,
  startDate: controlStartDate,
  // startTime: controlStartTime,
  initialFloorId,
  resourceId,
  reservationModalIsOpen,
  isReservationCardIsOpen,
  initialEndDate,
  isOnReservationModal,
  section: controlledSection,
  initialSearchText,
  initialTimeMode,
  disableUrlOfficeSync,
  selectionMode = false
}) => {
  // Let's manage some events "stateless" to prevent re-renders when not strictly necessary ( useful on huge maps )
  const events = useEventHandlerManager<MapProviderEvents>();
  const router = useRouter();
  const mapInstanceId = React.useId();
  const { reserveData, openReserveModal, isReserveOpen } = useReserve();

  const {
    userContext: { authenticatedUser, currentOffice: contextSelectedOffice },
    setUserContext
  } = useAuthenticated();
  const { setStartDate: setLayoutStartDate, startDate: commonStart } =
    useLayout();
  const [locateResourceId, setLocateResourceId] = React.useState<string | null>(
    null
  );

  // Pending locate request to be executed when the correct floor SVG is ready
  const pendingLocateRef = React.useRef<{
    resourceId: string;
    center: boolean;
    preserveData?: boolean;
  } | null>(null);

  // Pending reservation request to be executed when resources are loaded
  const pendingReservationRef = React.useRef<{
    resourceId: string;
  } | null>(null);

  // Pending resource selection from URL (non-reservation mode) to be executed when resources are loaded
  const pendingResourceSelectionRef = React.useRef<{
    resourceId: string;
    floorId?: string;
    center: boolean;
  } | null>(null);

  // Track if we've already auto-opened the reserve modal in reservation mode
  const hasAutoOpenedRef = React.useRef(false);
  // Track the last office ID to reset hasAutoOpenedRef when office changes
  const lastOfficeIdRef = React.useRef<string | null>(null);

  const [mapChangesCount, setMapChangesCount] = React.useState(0);
  const [mapBuilderSaveCount, setMapBuilderSaveCount] = React.useState(0);

  // Get command palette context to register map navigator and controllers
  const {
    registerMapNavigator,
    registerOpenResourceInfo,
    registerMapControllers
  } = useGlobalSearch();

  const defaultOfficeFromPropIsApplied = useRef(false);

  const currentOffice = useMemo(() => {
    const officeFromProp = controlledOfficeId
      ? authenticatedUser?.offices?.byId?.[controlledOfficeId]
      : undefined;

    // Embedded maps (previews with no floor switcher — dashboard cards,
    // request/reservation location previews) always show the office they
    // were given: it can legitimately differ from the app's globally
    // selected office (e.g. Live Map resolves the viewer's own desk's
    // office), and must keep doing so for the instance's whole lifetime,
    // not just its first render.
    if (isEmbedded || disableUrlOfficeSync) {
      return officeFromProp ?? contextSelectedOffice;
    }

    // The full /map page instead applies `officeId` only as an initial
    // default (so the user can still switch offices afterwards), locking
    // it in once the office data is actually available.
    if (officeFromProp && !defaultOfficeFromPropIsApplied.current) {
      defaultOfficeFromPropIsApplied.current = true;
      return officeFromProp;
    }
    return contextSelectedOffice;
  }, [
    controlledOfficeId,
    authenticatedUser?.offices?.byId,
    contextSelectedOffice,
    isEmbedded,
    disableUrlOfficeSync
  ]);

  const amenitiesResponse = useGetAmenities();
  const amenities = amenitiesResponse?.data?.result?.tags || [];

  const today = convertTZ(new Date(), currentOffice.time_zone);

  const urlParamsReady = React.useRef<boolean>(false);
  const isInitialized = React.useRef<boolean>(false);
  const lastSyncedUrlParams = React.useRef<string>("");
  const defaultsAppliedRef = React.useRef<boolean>(false);

  // Helper to get the correct timezone for initialization
  // If officeId prop is provided, use that office's timezone; otherwise use context office
  const getInitialTimeZone = () => {
    if (controlledOfficeId) {
      const officeFromProp =
        authenticatedUser?.offices?.byId?.[controlledOfficeId];
      if (officeFromProp) return officeFromProp.time_zone;
    }
    return contextSelectedOffice.time_zone;
  };

  const [_startDate, setStartDate] = React.useState<Date | null>(() => {
    const tz = getInitialTimeZone();
    if (controlStartDate) {
      if (controlStartDate.includes("T")) {
        return fromZonedTime(controlStartDate, tz);
      }
      return parseApiDate(controlStartDate);
    }
    if (commonStart) {
      return commonStart;
    }
    return new Date(convertTZ(new Date(), tz).getTime());
  });

  const [_endDate, setEndDate] = React.useState<Date | null>(() => {
    const tz = getInitialTimeZone();
    if (initialEndDate) {
      return fromZonedTime(initialEndDate, tz);
    }
    return null;
  });
  const [liveStartDate, setLiveStartDate] = React.useState<Date | null>(
    new Date()
  );
  const [liveEndDate, setLiveEndDate] = React.useState<Date | null>(null);

  const [resourcesIdFilter, setResourcesIdFilter] = React.useState<string[]>(
    []
  );
  const [filterQuery, setFilterQuery] = React.useState(initialSearchText || "");
  // Left undefined on purpose: with no explicit section the side panel opens
  // on whichever tab the organization put first (see MapFiltersCard), so a
  // hardcoded default here would silently override that setting.
  const [section, setSection] =
    React.useState<MapModalParams["defaultTab"]>(controlledSection);
  const [editGroupId, setEditGroupId] = React.useState<string | null>(null);
  const [openResourceModal, setOpenResourceModal] = React.useState(false);
  const [openSidePanel, setOpenSidePanel] = React.useState(false);
  const isTacticStaffAuthenticatedUser = !!isTacticStaff(authenticatedUser);

  const [timeMode, setTimeMode] = React.useState<StartEndTimeMode>("manual");

  const [resourceIdHovering, setResourceIdHovering] = React.useState<
    string | null
  >(null);

  const [selectedResource, setSelectedResource] =
    React.useState<IResource | null>(null);
  const [highlightedResource, setHighlightedResource] =
    React.useState<IResource | null>(null);
  const [focusedResource, setFocusedResource] = React.useState<string | null>(
    null
  );
  const [
    selectedUsersInCalendarIntegrations,
    setSelectedUsersInCalendarIntegrations
  ] = React.useState<
    {
      value: string;
      label: string;
      picture?: string;
      profile_photo_url?: string;
    }[]
  >([]);

  const startDate = timeMode === "manual" ? _startDate : liveStartDate;
  const endDate = timeMode === "manual" ? _endDate : liveEndDate;

  const mapFiltersDisclosure = useDisclosure();

  const editingMapDisclosure = useDisclosure<EditingMapDisclosureData>();
  const userFocusDisclosure = useDisclosure<IUserFull>();
  const fullscreenMapDisclosure = useDisclosure();
  const isMapEditing = editingMapDisclosure.isOpen;

  const [amenitiesFilter, setAmenitiesFilter] = React.useState<Tag[]>([]);

  const applyAmenitiesFilter = (ids: string[]) => {
    if (!amenities || amenities.length === 0) {
      setAmenitiesFilter([]);
      return;
    }
    const filtered = amenities.filter((amenity) => ids.includes(amenity.id));
    setAmenitiesFilter(filtered);
  };

  const {
    schedulable,
    floors,
    floorsWithResources,
    usersAtOffice,
    resources,
    loading: isDataLoading,
    floor,
    setFloor: _setFloor,
    reload: reloadResources
  } = useOfficeResources({
    officeId: disableUrlOfficeSync
      ? currentOffice.id
      : urlParamsReady.current
        ? currentOffice.id
        : null,
    startDate,
    endDate,
    filterQuery,
    entireOffice: isMapEditing || openSidePanel,
    getAllMaps: isTacticStaffAuthenticatedUser,
    coreApi: true,
    tags: amenitiesFilter.map((tag) => tag.id),
    floorId: controlledFloorId,
    initialFloorId,
    allowNoFloor: true
  });

  const changeOffice = React.useCallback(
    (officeId: string) => {
      setViewingDivision(null);
      if (officeId === currentOffice.id) {
        return;
      }

      // In modal/embedded contexts we must not navigate to `/map` when switching offices.
      // We rely on the modal URL sync (or parent page state) and only update the user context.
      if (
        disableUrlOfficeSync ||
        reservationModalIsOpen ||
        isOnReservationModal ||
        isEmbedded
      ) {
        setUserContext({
          type: "updateCurrentOffice",
          payload: officeId
        });
        return;
      }

      const mapLink = createMapLink({
        date: startDate ? format(startDate, "yyyy-MM-dd'T'HH:mm:ss") : "",
        endTime: endDate ? format(endDate, "yyyy-MM-dd'T'HH:mm:ss") : "",
        officeId: officeId,
        timeMode: timeMode
      });

      // Store the office ID we're navigating to, to prevent URL sync race condition
      // (the useEffect runs before router.query updates)
      pendingOfficeNavigation = officeId;

      router.replace(mapLink, undefined, { shallow: true });

      setUserContext({
        type: "updateCurrentOffice",
        payload: officeId
      });
    },
    [
      currentOffice.id,
      startDate,
      timeMode,
      endDate,
      router,
      setUserContext,
      disableUrlOfficeSync,
      reservationModalIsOpen,
      isOnReservationModal,
      isEmbedded
    ]
  );

  // Campus map view: when set, the map page renders this division's map
  // instead of the current floor. Cleared by any office/floor navigation.
  const [viewingDivision, setViewingDivision] =
    React.useState<Division | null>(null);

  const setFloor = (newFloor: IResource | null) => {
    setViewingDivision(null);
    if (newFloor?.id === floor?.id) return;

    _setFloor(newFloor);
    setStorageFloor(newFloor?.id);
    setOpenResourceModal(false);
    setSelectedResource(null);
    setHighlightedResource(null);
    setFocusedResource(null);

    events.trigger("onChangeFloor", newFloor, floor);
  };

  // Zone-link navigation (campus map → office floor): the target office's
  // floors only exist after the office switch propagates and refetches, so
  // the floor selection is parked here until they arrive.
  const pendingLinkFloorRef = React.useRef<{
    officeId: string;
    floorId?: string;
  } | null>(null);

  const navigateToOfficeFloor = (officeId: string, floorId?: string) => {
    if (officeId === currentOffice.id) {
      if (floorId) {
        const targetFloor = floors?.find((f) => f.id === floorId);
        if (targetFloor) setFloor(targetFloor);
      }
      return;
    }
    pendingLinkFloorRef.current = { officeId, floorId };
    changeOffice(officeId);
  };

  React.useEffect(() => {
    const pending = pendingLinkFloorRef.current;
    if (!pending || !floors?.length) return;
    // Floors from the previous office can still be in state right after the
    // switch — wait until the loaded floors belong to the target office.
    if (floors[0]?.office_id !== pending.officeId) return;
    pendingLinkFloorRef.current = null;
    if (!pending.floorId) return;
    const targetFloor = floors.find((f) => f.id === pending.floorId);
    if (targetFloor) setFloor(targetFloor);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [floors]);

  // Update the current floor's map_data in state without requiring a refetch.
  // Used after saving in the map builder so the view renders the new data
  // immediately when the editor closes.
  const updateFloorMapData = React.useCallback(
    (mapData: string) => {
      if (!floor) return;
      _setFloor({ ...floor, map_data: mapData });
      setMapBuilderSaveCount((c) => c + 1);
    },
    [floor, _setFloor]
  );

  React.useEffect(() => {
    if (timeMode !== "live") return;
    let timeoutId: NodeJS.Timeout | undefined;
    let recurringTimeoutId: NodeJS.Timeout;

    function setLiveTick() {
      const now = new Date();
      setLiveStartDate(now); // 30 minutes ago
      setLiveEndDate(addMinutes(now, 1));
      reloadResources();
    }

    function startUpdates() {
      setLiveTick();

      // Schedule next update in 60s
      recurringTimeoutId = setTimeout(startUpdates, 60000);
    }

    const now = new Date();
    const msToNextMinutePlus5s = (60 - now.getSeconds()) * 1000;

    setLiveTick();
    timeoutId = setTimeout(() => {
      startUpdates();
    }, msToNextMinutePlus5s);

    return () => {
      clearTimeout(timeoutId);
      clearTimeout(recurringTimeoutId);
    };
  }, [timeMode]);

  const defaultOfficeHoursCalculator = useDefaultOfficeHours(currentOffice);

  /**
   * Normalizes a date by zeroing out seconds and milliseconds.
   * This prevents drift from fractional seconds while preserving custom times
   * (e.g., 10:07 remains 10:07, not rounded to 10:00 or 10:15).
   * The 1-minute offset bug was caused by seconds/milliseconds in time calculations,
   * not by minutes being off-interval.
   */
  const normalizeTimePrecision = React.useCallback((date: Date): Date => {
    const normalized = new Date(date);
    normalized.setSeconds(0, 0);
    return normalized;
  }, []);

  /**
   * Checks if a date's minutes are on the configured time interval.
   * Used to determine if we should round to time intervals when preserving duration.
   */
  const isOnTimeInterval = React.useCallback((date: Date): boolean => {
    const minutes = date.getMinutes();
    return minutes % TIME_SELECT_INTERVAL_MINUTES === 0;
  }, []);

  const getOrCalculateDefaultEndDate = React.useCallback(
    (start: Date, providedEnd?: Date) => {
      if (providedEnd && providedEnd.getTime() > start.getTime())
        return providedEnd;

      const defaults = defaultOfficeHoursCalculator(start);
      if (defaults) {
        const end = new Date(start.getTime());
        setEndTimeWithDayOverflow(
          end,
          start,
          defaults.endHour,
          defaults.endMinutes
        );
        if (end.getTime() > start.getTime()) return end;
      }

      // Last resort: +1 hour
      return new Date(start.getTime() + 60 * 60 * 1000);
    },
    [defaultOfficeHoursCalculator]
  );

  const changeEndTime = React.useCallback(
    (date: Date | null) => {
      if (
        date === null ||
        startDate === null ||
        date.getTime() - startDate.getTime() <= 0
      ) {
        // avoid querying api with invalid values
        return;
      }
      // Only zero out seconds/milliseconds to prevent drift, preserve custom minutes
      const normalizedEndDate = normalizeTimePrecision(date);
      setEndDate(normalizedEndDate);
    },
    [startDate, normalizeTimePrecision]
  );

  const changeStartDate = React.useCallback(
    (date: Date | null) => {
      if (date === null) {
        return;
      }
      const defaultHours = defaultOfficeHoursCalculator(date);
      let updateDefaultHours = false;

      // Only zero out seconds/milliseconds to prevent drift, preserve custom minutes
      const normalizedDate = normalizeTimePrecision(date);

      // Check if the incoming date has a specific time set (not midnight)
      const hasSpecificTime =
        normalizedDate.getHours() !== 0 ||
        normalizedDate.getMinutes() !== 0 ||
        normalizedDate.getSeconds() !== 0;

      // Apply default office hours if:
      // 1. The day changed AND no specific time is set, OR
      // 2. We're in reservation mode and the time is midnight (from Agenda)
      const shouldApplyDefaultHours =
        (startDate &&
          !isSameDay(normalizedDate, startDate) &&
          !hasSpecificTime) ||
        (reservationModalIsOpen && !hasSpecificTime);

      if (shouldApplyDefaultHours && defaultHours) {
        normalizedDate.setHours(
          defaultHours.startHour,
          defaultHours.startMinutes,
          0
        );
        updateDefaultHours = true;
      }

      // When one of the bounds is missing (e.g. "live" mode with no end date yet),
      // fall back to office defaults so startDate can still move.
      // Normalize seconds/milliseconds before calculating difference to prevent drift,
      // but preserve minutes to support custom times (e.g., 10:07)
      const effectiveStart = startDate
        ? normalizeTimePrecision(startDate)
        : normalizedDate;
      const effectiveEnd = endDate
        ? normalizeTimePrecision(endDate)
        : getOrCalculateDefaultEndDate(effectiveStart);

      // Check if original times were on time intervals - if so, we'll round the result
      const startWasOnInterval = startDate
        ? isOnTimeInterval(startDate)
        : isOnTimeInterval(normalizedDate);
      const endWasOnInterval = endDate ? isOnTimeInterval(endDate) : false;

      let currentDiff = effectiveEnd.getTime() - effectiveStart.getTime();

      // If durations are collapsed or invalid, default to office defaults
      if (currentDiff <= 0) {
        const fallbackEnd = getOrCalculateDefaultEndDate(effectiveStart);
        currentDiff = fallbackEnd.getTime() - effectiveStart.getTime();
      }

      setStartDate(normalizedDate);
      setLayoutStartDate(normalizedDate);

      if (updateDefaultHours && defaultHours && endDate) {
        setEndTimeWithDayOverflow(
          endDate,
          normalizedDate,
          defaultHours.endHour,
          defaultHours.endMinutes
        );
      }

      // If endDate exists and is on the same day, we might not need to update it
      // But if endDate is null, we always need to set it
      if (endDate && isSameDay(endDate, normalizedDate)) {
        return;
      }
      const maxTime = new Date(normalizedDate);
      maxTime.setHours(23, 30, 0);

      // Calculate new end date and normalize seconds/milliseconds
      const newEndDate = normalizeTimePrecision(
        new Date(normalizedDate.getTime() + currentDiff)
      );

      // If both original times were on time intervals, round the result to time interval
      // This preserves the behavior for standard interval selections while allowing custom times
      if (startWasOnInterval && (endWasOnInterval || !endDate)) {
        const minutes = newEndDate.getMinutes();
        const roundedMinutes =
          Math.round(minutes / TIME_SELECT_INTERVAL_MINUTES) *
          TIME_SELECT_INTERVAL_MINUTES;
        newEndDate.setMinutes(roundedMinutes, 0, 0);
      }

      if (updateDefaultHours && defaultHours) {
        setEndTimeWithDayOverflow(
          newEndDate,
          normalizedDate,
          defaultHours.endHour,
          defaultHours.endMinutes
        );
      }

      // Ensure newEndDate is valid - if it's before normalizedDate, use maxTime or recalculate
      if (newEndDate.getTime() <= normalizedDate.getTime()) {
        // If the calculated end date is invalid, use maxTime for same day or recalculate with defaults
        const recalculatedEnd = getOrCalculateDefaultEndDate(normalizedDate);
        setEndDate(
          isSameDay(normalizedDate, recalculatedEnd) ? recalculatedEnd : maxTime
        );
      } else {
        setEndDate(
          isSameDay(normalizedDate, newEndDate) ? newEndDate : maxTime
        );
      }
    },
    [
      endDate,
      defaultOfficeHoursCalculator,
      startDate,
      setLayoutStartDate,
      normalizeTimePrecision,
      isOnTimeInterval,
      getOrCalculateDefaultEndDate
      // reservationModalIsOpen // this one doesn't change
    ]
  );

  // Change both start and end time atomically (avoids closure issues with separate callbacks)
  const changeTimeRange = React.useCallback(
    (newStart: Date, newEnd: Date) => {
      if (newEnd.getTime() - newStart.getTime() <= 0) {
        // Invalid range - end must be after start
        return;
      }
      setStartDate(newStart);
      setEndDate(newEnd);
      setLayoutStartDate(newStart);
    },
    [setLayoutStartDate]
  );

  // Keep track of current state in a ref to access it in useEffect without triggering re-runs
  const stateRef = React.useRef({ startDate, endDate, floor });
  stateRef.current = { startDate, endDate, floor };

  /* 
    Unified Initialization Effect 
    Handles:
    1. URL Parsing (Hash & Query)
    2. Office switching if URL differs
    3. Floor switching if URL differs
    4. Date/Time initialization (URL params > Defaults)
    5. Reservation mode setup
  */
  React.useEffect(() => {
    if (!router.isReady) {
      return;
    }
    urlParamsReady.current = true;

    // Parse URL (support both hash-based old URLs and standard query params)
    const [path, hash] = ((router.asPath as string) || "").split("#");
    const parsedHash = new URLSearchParams(hash);

    // Priority: Prop > Hash Param > Query Param
    const getParam = (name: string, queryFallback?: string) => {
      const val = parsedHash.get(name);
      if (val) return val;

      const qKey = queryFallback || name;
      if (router.query[qKey]) return String(router.query[qKey]);

      return null;
    };

    const resourceIdStr = resourceId || getParam("resource", "resourceId");
    const officeIdStr = getParam("office", "officeId");
    const floorIdStr = controlledFloorId || getParam("floor", "floorId");

    // Date/Time params
    // Note: 'date' in query usually implies startDate
    const dateStr =
      controlStartDate || getParam("date") || getParam("startTime");
    const startTimeStr = getParam("startTime") || getParam("date");
    const endDateStr = initialEndDate || getParam("endTime");
    const timeModeStr = getParam("timeMode") as StartEndTimeMode | null;

    // 1. Office Check
    let existingOffice: Office | null = null;
    if (!disableUrlOfficeSync) {
      // Skip URL sync if we're navigating to a specific office
      // (prevents race condition where URL hasn't updated yet but currentOffice has)
      if (
        pendingOfficeNavigation &&
        currentOffice.id === pendingOfficeNavigation
      ) {
        // Clear the pending flag once the URL has caught up
        if (officeIdStr === pendingOfficeNavigation) {
          pendingOfficeNavigation = null;
        }
      } else if (
        !controlledOfficeId &&
        officeIdStr &&
        currentOffice.id !== officeIdStr
      ) {
        existingOffice =
          authenticatedUser.offices?.offices.find(
            (o) => o.id === officeIdStr
          ) ?? null;
        if (existingOffice) {
          setUserContext({ type: "updateCurrentOffice", payload: officeIdStr });
        }
      }
    }

    // 2. Build current URL params string to check for changes
    const currentParamsString = JSON.stringify({
      resourceIdStr,
      officeIdStr,
      floorIdStr,
      dateStr,
      startTimeStr,
      endDateStr,
      timeModeStr
    });

    const urlParamsChanged =
      currentParamsString !== lastSyncedUrlParams.current;

    // If we are already initialized and the relevant URL params haven't changed,
    // we should NOT re-run the initialization logic (which would overwrite manual state).
    // EXCEPT if we are waiting for office defaults to be applied and they haven't been yet.
    const shouldSkipInitialization =
      isInitialized.current &&
      !urlParamsChanged &&
      (!!dateStr || defaultsAppliedRef.current);

    if (shouldSkipInitialization) {
      return;
    }

    // 0. Time Mode Sync
    const effectiveTimeModeSync = timeModeStr || timeMode;
    if (timeModeStr && timeModeStr !== timeMode) {
      if (timeModeStr === "live" || timeModeStr === "manual") {
        setTimeMode(timeModeStr);
      }
    }

    // TODO - REMOVED FOR NOW BECAUSE IT BREAKS THE FLOOR SWITCH. WE SHOULD INVESTIGATE ABOUT RESERVE MODAL
    // 3. Floor Check
    /*    if (floorIdStr && floorIdStr !== stateRef.current.floor?.id) {
      const floorExists = floors?.find((x) => floorIdStr === x.id);
      console.log("FLOOR EXISTS", floorExists);
      if (floorExists) {
        setFloor(floorExists);
        if (!controlledFloorId) {
          setStorageFloor(floorIdStr);
        }
      }
    }*/

    // 3. Date/Time Initialization
    // We only want to run initialization if we haven't set up valid dates yet OR if URL changes
    // But since this effect runs on deps, we need to be careful not to overwrite user interactions.
    // However, the goal is "Sync with URL".

    const tz = existingOffice?.time_zone || currentOffice.time_zone;

    // Determine target Start Date
    let targetStartDate: Date | null = null;
    if (dateStr) {
      // If full ISO string or just date. The two possible sources of a "T"
      // string encode it differently: a `startDate` prop (e.g. the Home map
      // preview, the reservation modal) is a naive wall-clock value in the
      // resolved office's own timezone — matching how the initial
      // `_startDate` state above parses `controlStartDate` via
      // `fromZonedTime`. A `date`/`startTime` URL or hash param, however, is
      // written by this same provider's own URL-sync (`changeOffice`, the
      // office-switch handler) via a plain `format(startDate, ...)` with no
      // timezone conversion — i.e. the viewer's own local time — so it must
      // be read back with `parseISO` the same way, or the round-trip drifts
      // by the local/office offset every time this effect re-runs.
      if (dateStr.includes("T")) {
        targetStartDate = controlStartDate
          ? fromZonedTime(dateStr, tz)
          : parseISO(dateStr);
      } else {
        targetStartDate = parseApiDate(dateStr);
      }
    }

    // Determine target End Date
    let targetEndDate: Date | null = null;
    if (endDateStr) {
      if (endDateStr.includes("T")) {
        targetEndDate = initialEndDate
          ? fromZonedTime(endDateStr, tz)
          : parseISO(endDateStr);
      } else {
        targetEndDate = parseApiDate(endDateStr);
      }
    }
    // Logic for applying defaults if NO URL params
    // const defaultOfficeHours = defaultOfficeHoursCalculator(
    //   targetStartDate || today
    // );

    // if (
    //   targetStartDate &&
    //   dateStr &&
    //   !dateStr.includes("T") &&
    //   defaultOfficeHours
    // ) {
    //   targetStartDate.setHours(
    //     defaultOfficeHours.startHour,
    //     defaultOfficeHours.startMinutes,
    //     0
    //   );
    // }

    // Access current state from ref to avoid dependency cycle
    const currentStart = stateRef.current.startDate
      ? new Date(stateRef.current.startDate)
      : null;
    const currentEnd = stateRef.current.endDate
      ? new Date(stateRef.current.endDate)
      : null;

    if (targetStartDate) {
      // URL/Prop provided a target date (and potentially times)
      const startChanged =
        !currentStart ||
        Math.abs(targetStartDate.getTime() - currentStart.getTime()) > 1000;

      const endChanged =
        targetEndDate &&
        (!currentEnd ||
          Math.abs(targetEndDate.getTime() - currentEnd.getTime()) > 1000);

      // If we have a date but NO specific end time in the URL, apply office defaults
      const isDateOnlyFromUrl =
        dateStr &&
        !dateStr.includes("T") &&
        !startTimeStr?.includes("T") &&
        !endDateStr;
      const isMissingEndTimeFromUrl = (dateStr || startTimeStr) && !endDateStr;

      let finalTargetStart = targetStartDate;
      let finalTargetEnd = targetEndDate;

      if (
        (isDateOnlyFromUrl || isMissingEndTimeFromUrl) &&
        effectiveTimeModeSync !== "live"
      ) {
        const defaultOfficeHours = defaultOfficeHoursCalculator(
          targetStartDate || today
        );
        if (defaultOfficeHours) {
          if (isDateOnlyFromUrl) {
            finalTargetStart = new Date((targetStartDate || today).getTime());
            finalTargetStart.setHours(
              defaultOfficeHours.startHour,
              defaultOfficeHours.startMinutes,
              0
            );
          }

          if (isMissingEndTimeFromUrl) {
            finalTargetEnd = new Date(
              (targetStartDate || finalTargetStart || today).getTime()
            );
            const startForEnd = finalTargetStart || targetStartDate || today;
            setEndTimeWithDayOverflow(
              finalTargetEnd,
              startForEnd,
              defaultOfficeHours.endHour,
              defaultOfficeHours.endMinutes
            );
          }
        }
      }

      // Validate end date against start date
      const effectiveEndDate = getOrCalculateDefaultEndDate(
        finalTargetStart || today,
        finalTargetEnd || undefined
      );

      if (
        startChanged ||
        endChanged ||
        isDateOnlyFromUrl ||
        (isMissingEndTimeFromUrl && effectiveTimeModeSync !== "live")
      ) {
        setStartDate(finalTargetStart);
        setEndDate(effectiveEndDate);

        if (finalTargetStart) {
          setLayoutStartDate(finalTargetStart);
        }
      }
    } else {
      // No URL/Prop date -> Apply Defaults (if not already set correctly)
      // We assume if startDate is "today midnight" (initial state) we might want to apply defaults
      // Or if we strictly want to enforce "On Load, if no URL, set to Office Hours".
      const defaultOfficeHours = defaultOfficeHoursCalculator(
        targetStartDate || today
      );
      if (defaultOfficeHours) {
        defaultsAppliedRef.current = true;
        // Calculate today's default hours
        const initializedDate = new Date(
          (_startDate || new Date(convertTZ(new Date(), tz))).getTime()
        );
        initializedDate.setHours(
          defaultOfficeHours.startHour,
          defaultOfficeHours.startMinutes,
          0
        );

        // Check if we are already seemingly initialized (to avoid loop)
        // But for now, let's enforce defaults if no URL params found

        // Only update if current is different from target default
        const startDiffers =
          !currentStart ||
          Math.abs(currentStart.getTime() - initializedDate.getTime()) > 60000;

        const initializedEnd = new Date(initializedDate);
        setEndTimeWithDayOverflow(
          initializedEnd,
          initializedDate,
          defaultOfficeHours.endHour,
          defaultOfficeHours.endMinutes
        );
        const endDiffers =
          !currentEnd ||
          Math.abs(currentEnd.getTime() - initializedEnd.getTime()) > 60000;

        if (startDiffers || endDiffers) {
          setLayoutStartDate(initializedDate);
          setStartDate(initializedDate);
          setEndDate(initializedEnd);
        }
      }
    }

    // 4. Reservation Mode
    if (
      reservationModalIsOpen &&
      resourceIdStr &&
      !pendingReservationRef.current
    ) {
      pendingReservationRef.current = { resourceId: resourceIdStr };
    }

    // 5. Non-reservation mode resource selection from URL
    // When navigating to map with a resourceId (e.g., from global search clicking on a person),
    // queue the resource selection to be executed when resources are loaded
    if (
      !reservationModalIsOpen &&
      resourceIdStr &&
      !pendingResourceSelectionRef.current
    ) {
      pendingResourceSelectionRef.current = {
        resourceId: resourceIdStr,
        floorId: floorIdStr || undefined,
        center: true
      };
    }

    // Mark as initialized and store current params
    isInitialized.current = true;
    lastSyncedUrlParams.current = currentParamsString;
  }, [
    router.isReady,
    router.asPath,
    // This effect should only run on initialization or context switch

    // Add stable dependencies
    currentOffice.id,
    currentOffice.time_zone,
    authenticatedUser.offices?.offices,
    floors, // dependency for floor check
    // REMOVED: startDate, endDate, floor?.id (tracked via stateRef)
    controlledOfficeId,
    controlledFloorId,
    controlStartDate,
    initialEndDate,
    resourceId,
    reservationModalIsOpen,
    defaultOfficeHoursCalculator,
    timeMode
    // actions like setFloor, changeStartDate are stable refs (hopefully) or we should check them
  ]);

  const [forceMapBuilder, setForceMapBuilder] = React.useState(false);
  const [isPinRequestMode, setIsPinRequestMode] = React.useState(false);
  const mapData = floor?.map_data ? JSON.parse(floor?.map_data) : undefined;
  const isMapBuilderMap = forceMapBuilder || (!floor?.map_url && !!mapData);

  const poppers = useMapViewOverlaysPopper(false);

  const highlightResource: MapStateActions["highlightResource"] =
    React.useCallback(
      (resId, target) => {
        if (!schedulable || !resId) {
          setHighlightedResource(null);
          return;
        }
        const res = schedulable?.find((x) => x.id === resId);
        if (!res) {
          poppers.setReferenceDetailsHighlightElement(null);
          setHighlightedResource(null);
          return;
        }
        if (isMapBuilderMap) {
          const targetOption = target as MapInteractiveZoneActionOptions;
          poppers.setHighlightResourceCoords({
            x: targetOption.x || 0,
            y: targetOption.y || 0,
            width: targetOption.width || 0,
            height: targetOption.height || 0
          });
        } else {
          poppers.setReferenceDetailsHighlightElement(target as Element);
        }
        setHighlightedResource(res);
      },
      [schedulable, isMapBuilderMap]
    );

  const startReserve = (resource: IResource, preserveData = false) => {
    // When the caller asks us to preserve the form data (e.g. duplicate
    // or edit flow) and we have reserveData already loaded, re-open the
    // reserve modal merging the preserved data with the newly focused
    // resource so title, attenders, time, etc. aren't lost. Otherwise
    // just trigger the focus event and let MapReserveCard's
    // onFocusResource handler compute defaults for a fresh reservation.
    if (preserveData && reserveData) {
      openReserveModal({ ...reserveData, resource }, false, true);
    }
    events.trigger("onFocusResource", resource);
  };

  const filteredResources = React.useMemo(() => {
    if (!resources) return [];

    if (!resourcesIdFilter || resourcesIdFilter.length === 0) {
      return resources;
    }
    return resources.filter((resource) =>
      resourcesIdFilter.includes(resource.id)
    );
  }, [resources, resourcesIdFilter]);

  const allowEditingPointsAndUploads =
    isTacticStaffAuthenticatedUser ||
    authenticatedUser?.user?.is_being_impersonated;

  const selectResource = React.useCallback(
    (
      resId: string,
      center = false,
      callback?: () => void,
      officeId?: string,
      floorId?: string,
      date?: Date,
      preserveData = false
    ) => {
      if (!schedulable && !resources) {
        return;
      }
      const res =
        schedulable?.find((x) => x.id === resId) ||
        resources?.find((x) => x.id === resId);

      if (officeId && res?.office_id && officeId !== res.office_id) {
        console.warn("Attempted to select resource from different office");
        return;
      }

      // In selection mode the map is only a picker, so skip the edit-group side
      // effect — only `selectedResource` should change on a click. In pin-request
      // mode the map is a pin picker, so a resource click is a no-op entirely.
      if (allowEditingPointsAndUploads && !selectionMode && !isPinRequestMode) {
        setEditGroupId(resId);
      }

      // Determine target floor - use provided floorId if available, otherwise find it from resources
      const targetFloor = floorId
        ? floorsWithResources?.find((f) => f.id === floorId)
        : floorsWithResources?.find((f) =>
            f.resources.find((x) => x.id === resId)
          );

      const needsFloorSwitch = targetFloor && floor?.id !== targetFloor.id;

      const openOnCurrentFloor = () => {
        //setOpenSidePanel(false);
        highlightResource(null, null);

        if (reservationModalIsOpen && res) {
          startReserve(res, preserveData);
          return;
        }

        if (!center) {
          // Not centering - open modal immediately. In selection mode only the
          // selection should change; don't open the resource modal/popover.
          // In pin-request mode the map is a pin picker for an empty point, so a
          // resource click must not change the (hidden) selection at all.
          if (!isPinRequestMode) {
            setSelectedResource(res || null);
            // Normally, only open the card when the clicked resource was
            // actually resolved — opening with a null selection renders a
            // blank popover (the lists can miss the id mid-refetch / during a
            // floor switch). Matches the `if (!res) return` guard used by the
            // locate/floor-ready paths. Editors are the exception: clicking an
            // unassigned map node (no resource ever attached) also resolves
            // `res` to null, and that's exactly the click that's supposed to
            // open the popover's "Assign Node Resource" action for them.
            if ((res || allowEditingPointsAndUploads) && !selectionMode) {
              setOpenResourceModal(true);
              poppers.setReferenceResDetailsElement(
                document.getElementById(`interactive-${resId}`)
              );
            }
          }
        } else {
          // For centering - trigger zoom; modal will be opened via locate callback
          setLocateResourceId(resId);
        }

        callback?.();
      };

      // If we need to switch floors, do it first and queue the action
      if (targetFloor && needsFloorSwitch) {
        setFloor(targetFloor);
        setStorageFloor(targetFloor.id);
        if (reservationModalIsOpen && res) {
          // Queue startReserve for after the floor SVG renders
          pendingLocateRef.current = {
            resourceId: resId,
            center: false,
            preserveData
          };
        } else {
          pendingLocateRef.current = {
            resourceId: resId,
            center: !!center
          };
        }
        return;
      }

      // Same floor — open reserve or resource modal immediately
      if (reservationModalIsOpen && res) {
        startReserve(res, preserveData);
        return;
      }

      // Same floor – we can act immediately
      openOnCurrentFloor();
    },
    [
      setFloor,
      floor,
      floorsWithResources,
      resources,
      schedulable,
      allowEditingPointsAndUploads,
      selectionMode,
      isPinRequestMode,
      highlightResource,
      poppers,
      setLocateResourceId,
      setOpenResourceModal,
      setSelectedResource,
      pendingLocateRef
    ]
  );

  // Auto-open reserve modal when in reservation mode
  React.useEffect(() => {
    // >> this is a sus part we add here to avoid some glitches on map modal
    // Track if office changed to prevent auto-opening when user manually changes office
    const officeJustChanged =
      lastOfficeIdRef.current !== null &&
      lastOfficeIdRef.current !== currentOffice.id;
    if (officeJustChanged) {
      hasAutoOpenedRef.current = false;
      lastOfficeIdRef.current = currentOffice.id;
    }
    lastOfficeIdRef.current = currentOffice.id;
    // << try removing it if weird bug occurs

    // If we have a specific resourceId, wait for resources to load
    if (pendingReservationRef.current && schedulable) {
      const targetResource = schedulable.find(
        (r) => r.id === pendingReservationRef.current?.resourceId
      );

      if (targetResource) {
        // Use selectResource to ensure we're on the right floor/view
        selectResource(targetResource.id, false);
        pendingReservationRef.current = null;
        hasAutoOpenedRef.current = true;
      }
    }
    // If in reservation mode without resourceId, open reserve modal with first available resource
    else if (
      reservationModalIsOpen &&
      !hasAutoOpenedRef.current &&
      !officeJustChanged &&
      schedulable &&
      schedulable.length > 0 &&
      floor
    ) {
      // Check if we have a resource from duplicate/edit data
      if (reserveData?.resource) {
        const resInSchedulable = schedulable.find(
          (r) => r.id === reserveData.resource?.id
        );
        if (resInSchedulable) {
          // Resource is on the current floor — select it directly
          selectResource(
            reserveData.resource.id,
            false,
            undefined,
            undefined,
            undefined,
            undefined,
            true
          );
        } else {
          // Resource is on a different floor — switch floor and queue for SVG ready
          const targetFloor = floorsWithResources?.find((f) =>
            f.resources.some((r) => r.id === reserveData.resource?.id)
          );
          if (targetFloor && targetFloor.id !== floor?.id) {
            setFloor(targetFloor);
            pendingLocateRef.current = {
              resourceId: reserveData.resource.id,
              center: false,
              preserveData: true
            };
          } else {
            // Fallback — just open reserve without focusing
            startReserve(reserveData.resource as IResource, true);
          }
        }
        hasAutoOpenedRef.current = true;
        return;
      }

      hasAutoOpenedRef.current = true;
    }
  }, [
    schedulable,
    startReserve,
    reservationModalIsOpen,
    // startDate,
    floor,
    floorsWithResources,
    currentOffice,
    openReserveModal,
    selectResource,
    setFloor,
    reserveData
  ]);

  // Handle pending resource selection from URL (non-reservation mode)
  // This handles the case when navigating to /map from global search with a resourceId
  // We need to wait for resources to load before selecting
  React.useEffect(() => {
    if (!pendingResourceSelectionRef.current || !schedulable) {
      return;
    }

    const pending = pendingResourceSelectionRef.current;
    const targetResource = schedulable.find((r) => r.id === pending.resourceId);

    if (targetResource) {
      selectResource(
        targetResource.id,
        pending.center,
        undefined,
        undefined,
        pending.floorId
      );
      pendingResourceSelectionRef.current = null;
    }
  }, [schedulable, selectResource]);

  const [zonesInfo, setZonesInfo] = React.useState<InteractiveZonesInfo>({
    mapInfo: null,
    zones: []
  });

  // Keep refs to the latest functions
  const selectResourceRef = React.useRef(selectResource);
  const openResourceInfoRef = React.useRef<
    ((resourceId: string) => void) | null
  >(null);

  // Update ref when selectResource changes
  React.useEffect(() => {
    selectResourceRef.current = selectResource;
  }, [selectResource]);

  // Create the openResourceInfo function and keep it in a ref
  React.useEffect(() => {
    openResourceInfoRef.current = (resourceId: string) => {
      // Wait a bit for the DOM to settle after zoom, then open the modal
      setTimeout(() => {
        // Prefer schedulable resource, but fall back to all resources on the floor.
        const resFromSchedulable = schedulable?.find(
          (x) => x.id === resourceId
        );
        const resFromAll = resources?.find((x) => x.id === resourceId);
        const res = resFromSchedulable || resFromAll;

        if (!res) {
          return;
        }

        setSelectedResource(res);
        setOpenResourceModal(true);

        // Wait for the element to be in the DOM and properly positioned
        setTimeout(() => {
          const element = document.getElementById(`interactive-${resourceId}`);
          if (element) {
            poppers.setReferenceResDetailsElement(element);
          }
        }, 300);
      }, 300);
    };
  }, [
    schedulable,
    resources,
    setSelectedResource,
    setOpenResourceModal,
    poppers
  ]);

  // Open full resource details when a locate event completes (e.g. URL-based navigation)
  React.useEffect(() => {
    if (isOnReservationModal) {
      return;
    }
    const handleLocateResource = (resource: IResource | null) => {
      if (!resource) return;

      // For locate events we already have the full resource object,
      // so open the modal directly without depending on schedulable/resources
      // lookup timing.
      setSelectedResource(resource);
      setOpenResourceModal(true);

      // Wait for the element to be in the DOM and properly positioned
      setTimeout(() => {
        const element = document.getElementById(`interactive-${resource.id}`);
        if (element) {
          poppers.setReferenceResDetailsElement(element);
        }
      }, 100);
    };

    events.register("onLocateResource", handleLocateResource);

    return () => {
      events.unregister("onLocateResource", handleLocateResource);
    };
  }, [events, setSelectedResource, setOpenResourceModal, poppers]);

  // Called by floor items when their SVG is ready. If we have a pending locate
  // request targeting this floor, start the locate/zoom flow now.
  const onFloorReady = React.useCallback(
    (floorId: string) => {
      const pending = pendingLocateRef.current;
      if (!pending) return;

      const resFromSchedulable =
        schedulable?.find((x) => x.id === pending.resourceId) || null;
      const resFromAll =
        resources?.find((x) => x.id === pending.resourceId) || null;
      const res = resFromSchedulable || resFromAll;

      if (!res) {
        pendingLocateRef.current = null;
        return;
      }

      // Ensure this resource actually belongs to this floor
      if (res.parent_id && res.parent_id !== floorId) {
        return;
      }

      pendingLocateRef.current = null;

      highlightResource(null, null);

      if (reservationModalIsOpen && res) {
        startReserve(res, pending.preserveData ?? false);
        return;
      }

      if (!pending.center) {
        setOpenResourceModal(true);
        setSelectedResource(res);
        poppers.setReferenceResDetailsElement(
          document.getElementById(`interactive-${res.id}`)
        );
      } else {
        setLocateResourceId(res.id);
      }
    },
    [
      schedulable,
      resources,
      highlightResource,
      setOpenResourceModal,
      setSelectedResource,
      poppers,
      reservationModalIsOpen,
      startReserve
    ]
  );

  // Register a stable wrapper function with command palette on mount only
  React.useEffect(() => {
    const stableSelectResource = (params: MapNavigationParams) => {
      const { resourceId, officeId, floorId, date, endDate, center, callback } =
        params;

      // Check if we need to change office or date first
      const needsOfficeChange = officeId && officeId !== currentOffice?.id;
      const needsDateChange =
        date &&
        (!_startDate || date.toDateString() !== _startDate.toDateString());

      if (needsOfficeChange || needsDateChange) {
        // Handle office and date changes first
        if (needsOfficeChange && officeId) {
          // Navigate to the office with the date if provided
          const targetOffice = authenticatedUser?.offices?.byId?.[officeId];
          const isToday =
            date && targetOffice
              ? isSameDay(date, convertTZ(new Date(), targetOffice.time_zone))
              : true;
          const effectiveTimeMode = isToday ? "live" : "manual";

          // Change office context
          setUserContext({
            type: "updateCurrentOffice",
            payload: officeId
          });

          // Set the time mode and date
          setTimeMode(effectiveTimeMode);
          if (date) {
            if (effectiveTimeMode === "live") {
              setTimeMode("live");
            } else {
              setStartDate(date);
              const end = getOrCalculateDefaultEndDate(date, endDate);
              setEndDate(end);
            }
          }

          // Wait for office change to propagate, then select resource
          setTimeout(() => {
            selectResourceRef.current(
              resourceId,
              center,
              callback,
              officeId,
              floorId,
              date,
              false
            );
          }, 600);
        } else if (needsDateChange && date) {
          // Only date change, no office change
          const targetOffice = currentOffice;
          const isToday = targetOffice
            ? isSameDay(date, convertTZ(new Date(), targetOffice.time_zone))
            : true;

          if (isToday) {
            setTimeMode("live");
          } else {
            setTimeMode("manual");
            setStartDate(date);
            const end = getOrCalculateDefaultEndDate(date, endDate);
            setEndDate(end);
          }

          // Wait for date change to propagate, then select resource
          setTimeout(() => {
            selectResourceRef.current(
              resourceId,
              center,
              callback,
              officeId,
              floorId,
              date
            );
          }, 300);
        }
      } else {
        // No office or date change needed, just select the resource
        selectResourceRef.current(
          resourceId,
          center,
          callback,
          officeId,
          floorId,
          date
        );
      }
    };

    registerMapNavigator(stableSelectResource);

    // Unregister when component unmounts
    return () => {
      registerMapNavigator(null);
    };
  }, [
    registerMapNavigator,
    currentOffice,
    _startDate,
    authenticatedUser,
    setUserContext,
    setTimeMode,
    setStartDate,
    setEndDate
  ]);

  // Register a stable wrapper for openResourceInfo with command palette on mount only
  React.useEffect(() => {
    const stableOpenResourceInfo = (resourceId: string) => {
      openResourceInfoRef.current?.(resourceId);
    };

    registerOpenResourceInfo(stableOpenResourceInfo);

    return () => {
      registerOpenResourceInfo(null);
    };
  }, [registerOpenResourceInfo]);

  // Register map controllers for office and time changes
  React.useEffect(() => {
    const controllers = {
      changeOffice: (officeId: string) => {
        setUserContext({
          type: "updateCurrentOffice",
          payload: officeId
        });
      },
      changeStartDate: (date: Date) => {
        // Use direct setter if the changeStartDate guard would reject it
        if (endDate === null || startDate === null) {
          setStartDate(date);
          // Set a default end date if none exists
          if (endDate === null) {
            const defaultEnd = getOrCalculateDefaultEndDate(date);
            setEndDate(defaultEnd);
          }
        } else {
          changeStartDate(date);
        }
      },
      changeEndTime: changeEndTime,
      setTimeMode: setTimeMode,
      navigateToOffice: (
        officeId: string,
        date?: Date,
        timeMode?: StartEndTimeMode,
        endDate?: Date
      ) => {
        // Use current startDate as default if date is not provided
        const effectiveDate = date || startDate || new Date();
        const effectiveEndDate = getOrCalculateDefaultEndDate(
          effectiveDate,
          endDate
        );

        // Determine if the date is today - if not, use manual mode instead of live
        const targetOffice = authenticatedUser?.offices?.byId?.[officeId];

        // keep this in case we need it later
        const isToday =
          effectiveDate && targetOffice
            ? isSameDay(
                effectiveDate,
                convertTZ(new Date(), targetOffice.time_zone)
              )
            : true;

        const effectiveTimeMode = timeMode || "manual";

        // First, update the URL with new office and parameters
        const mapLink = createMapLink({
          date: format(effectiveDate, "yyyy-MM-dd'T'HH:mm:ss"),
          endTime: effectiveEndDate
            ? format(effectiveEndDate, "yyyy-MM-dd'T'HH:mm:ss")
            : undefined,
          officeId: officeId,
          timeMode: effectiveTimeMode
        });
        router.replace(mapLink, undefined, { shallow: true });

        // Change the office - this will trigger a re-render with new office context
        setUserContext({
          type: "updateCurrentOffice",
          payload: officeId
        });

        // After office changes, set the date and time mode
        // Use setTimeout to ensure office context has updated and useEffect has run
        setTimeout(() => {
          setTimeMode(effectiveTimeMode);

          if (effectiveDate && targetOffice) {
            const dateInOfficeTimezone = convertTZ(
              effectiveDate,
              targetOffice.time_zone
            );

            setStartDate(dateInOfficeTimezone);
            const endDateInOfficeTimezone = convertTZ(
              effectiveEndDate,
              targetOffice.time_zone
            );
            setEndDate(endDateInOfficeTimezone);
          }
        }, 100);
      }
    };

    registerMapControllers(controllers);

    return () => {
      registerMapControllers(null);
    };
  }, [
    registerMapControllers,
    setUserContext,
    changeStartDate,
    changeEndTime,
    changeTimeRange,
    setTimeMode,
    startDate,
    endDate,
    setStartDate,
    setEndDate,
    authenticatedUser
  ]);

  return (
    <MapPageContext.Provider
      value={{
        schedulable,
        floors,
        floorsWithResources,
        usersAtOffice,
        resources: filteredResources,
        allResources: resources,
        floor,
        setFloor,
        updateFloorMapData,
        startDate,
        endDate,
        currentOffice,
        changeOffice,
        navigateToOfficeFloor,
        viewingDivision,
        setViewingDivision,
        changeEndTime,
        changeStartDate,
        changeTimeRange,
        timeMode,
        setTimeMode,
        isDataLoading,
        selectedResource,
        setSelectedResource,
        isMapEditing,
        highlightedResource,
        setHighlightedResource,
        isEmbedded,
        isMapBuilderMap,
        poppers,
        setEditGroupId,
        editGroupId,
        openResourceModal,
        setOpenResourceModal,
        highlightResource,
        mapBuilderData: mapData,
        setForceMapBuilder,
        editingMapDisclosure,
        resourceIdHovering,
        setResourceIdHovering,
        focusedResource,
        setFocusedResource,
        startReserve,
        setResourcesIdFilter,
        setStartDate,
        events,
        amenities,
        amenitiesFilter,
        applyAmenitiesFilter,
        userFocusDisclosure,
        selectResource,
        mapFiltersDisclosure,
        zonesInfo,
        setZonesInfo,
        locateResourceId,
        setLocateResourceId,
        liveStartDate,
        liveEndDate,
        mapChangesCount,
        mapBuilderSaveCount,
        setMapChangesCount,
        forceMapBuilder,
        filterQuery,
        setFilterQuery,
        onFloorReady,
        isReservationMode: !!reservationModalIsOpen,
        selectionMode,
        isPinRequestMode,
        setIsPinRequestMode,
        isOnReservationModal,
        resourceInReservation: reserveData?.resource?.id || null,
        fullscreenMapDisclosure,
        defaultTab: section,
        setSection,
        selectedUsersInCalendarIntegrations,
        setSelectedUsersInCalendarIntegrations,
        mapInstanceId,
        isReservationCardIsOpen:
          isReservationCardIsOpen || (isReserveOpen && !isOnReservationModal)
      }}
    >
      {children}
    </MapPageContext.Provider>
  );
};

export const useMap = () => {
  const context = React.useContext(MapPageContext);
  if (!context) {
    throw new Error("useMap must be used within a MapPageProvider");
  }
  return context;
};
